import { NextResponse } from 'next/server'
import type { QliroValidateOrderDeclineResponse, ShippingProviderName } from 'ecosystem'
import { createLogger } from 'shared-utils/utils/logger'
import storefrontApi from 'api/storefront-api'
import { validatePostnordShipping } from './utils'

const logger = createLogger('payment', 'qliro', 'validate')

export interface ValidateOrderOpts {
  orderId: string
  shippingProviderName?: ShippingProviderName
}

/**
 * Qliro's order-validation callback: answer 200 with an empty body to let the
 * purchase through, or 400 with a decline reason to stop it.
 *
 * The empty 200 is deliberate — Qliro reads the status, not a body. The pages
 * version wrote `res.status(200).end()`, which the App Router shim turned into
 * a JSON `null`; this restores the original shape.
 */
export const qliroValidateOrder = async (opts: ValidateOrderOpts): Promise<Response> => {
  // Fetch storefront order for shipping validation
  const { error, data: storefrontOrder } = await storefrontApi.orders.getStorefrontOrder({
    orderNumber: opts.orderId
  })

  if (error || !storefrontOrder) {
    logger.error('Failed to fetch storefront order for validation', {
      orderId: opts.orderId,
      error
    })

    return NextResponse.json(
      {
        DeclineReason: 'ORDER_FETCH_FAILED',
        DeclineReasonMessage: 'Could not validate the order. Please try again.'
      } satisfies QliroValidateOrderDeclineResponse,
      { status: 400 }
    )
  }

  // Validate shipping data on the storefront order
  let declineResponse: QliroValidateOrderDeclineResponse | null = null

  switch (opts.shippingProviderName) {
    case 'POSTNORD':
      declineResponse = validatePostnordShipping(storefrontOrder)
      break
    case 'NSHIFT':
      // todo: add NSHIFT validation
      break
    case 'INGRID':
      // todo: add INGRID validation
      break
    case 'DEFAULT':
    default:
      break
  }

  if (declineResponse) {
    return NextResponse.json(declineResponse, { status: 400 })
  }

  return new NextResponse(null, { status: 200 })
}
